/**
 * Catálogo das etapas de negócio da Realização Destaque, do Reconhecimento e da ECOA, reconhecidas
 * pela chamada que cada etapa já faz à API (spec OBS-12 a OBS-19).
 *
 * A URL sozinha não basta: aprovar e rejeitar uma realização são o mesmo `PUT database/bb_task__c`,
 * e excluir, aprovar participantes e cancelar reconhecimento são o mesmo `POST action/log`. O que
 * separa as etapas é o `status` ou o `actionId` do corpo. Do corpo saem só esses valores de
 * enumeração; mensagem, alvo, matrícula e nome nunca entram no evento.
 */

export type Flow = 'realizacao_destaque' | 'reconhecimento' | 'ecoa';

export type StepOutcome = 'sucesso' | 'recusado' | 'falha';

export interface ApiCall {
  method: string;
  url: string;
  body: unknown;
  /** Caminho da página no momento da resposta; decide o fluxo das actions comuns às duas entregas. */
  pagePath: string;
}

export interface ApiResult {
  httpStatus: number;
  responseData: unknown;
}

export type FlowStepAttributes = {
  fluxo: Flow;
  etapa: string;
  resultado: StepOutcome;
  http_status: string;
  status?: string;
  aprovado?: 'sim' | 'nao';
};

type FlowSource = Flow | 'pela_rota';

interface StepDefinition {
  flow: FlowSource;
  step: string;
  /** A trigger devolve o desfecho em `attributes.status`; diferente de `OK` é recusa, como nas telas. */
  readsTriggerOutcome: boolean;
}

/**
 * O `PUT` de status serve ao dono (edição e reenvio) e ao gestor (apreciação). Quem chamou só aparece
 * na rota: as telas de apreciação ficam em `MANAGER_PAGE_PREFIXES`.
 */
const STATUS_CHANGE = 'mudanca_de_status';

const step = (flow: FlowSource, name: string, readsTriggerOutcome = false): StepDefinition => ({
  flow,
  step: name,
  readsTriggerOutcome,
});

const DATABASE_STEPS: Record<string, Partial<Record<string, StepDefinition>>> = {
  bb_task__c: {
    post: step('realizacao_destaque', 'cadastro_enviado'),
    put: step('realizacao_destaque', STATUS_CHANGE),
  },
  bb_task_feedback__c: { post: step('realizacao_destaque', 'feedback_enviado') },
  bb_task_ecoa__c: {
    post: step('ecoa', 'cadastro_enviado'),
    put: step('ecoa', STATUS_CHANGE),
    delete: step('ecoa', 'excluida'),
  },
  bb_task_ecoa_feedback__c: { post: step('ecoa', 'feedback_enviado') },
  reward_behavior__c: {
    post: step('reconhecimento', 'enviado', true),
    put: step('reconhecimento', 'editado', true),
  },
};

const ACTION_STEPS: Record<string, StepDefinition> = {
  task_delete: step('realizacao_destaque', 'excluida', true),
  task_participants_approval: step('pela_rota', 'participantes_avaliados', true),
  task_team_member_approval: step('pela_rota', 'membro_avaliado', true),
  task_team_member_exclude: step('pela_rota', 'membro_excluido', true),
  task_team_member_change_percent: step('pela_rota', 'percentual_alterado', true),
  cancelar_reconhecimento: step('reconhecimento', 'cancelado', true),
  devolver_reconhecimento: step('reconhecimento', 'devolvido', true),
};

const ECOA_PAGE_PREFIXES = ['/acao-ecoa', '/validar-acao-ecoa', '/v3/acao-ecoa', '/v3/validar-acao-ecoa'];

const MANAGER_PAGE_PREFIXES = [
  '/minha-equipe/validar-apreciar-realizacao',
  '/v2/minha-equipe/validar-apreciar-realizacao',
  '/validar-destaque',
  '/validar-acao-ecoa',
  '/v3/validar-acao-ecoa',
];

const ENUM_VALUE = /^[a-z_]{1,32}$/i;

const DATABASE_PATH = /^database\/([^/]+)$/;

const ACTION_LOG_PATH = 'action/log';

/** `/database/x?q=...`, `database/x` e a URL absoluta viram `database/x`. */
function apiPath(url: string): string {
  const [withoutQuery] = url.split(/[?#]/);
  return withoutQuery
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\/+/, '')
    .replace(/^v3\//, '');
}

function parseBody(body: unknown): Record<string, any> {
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return body && typeof body === 'object' ? (body as Record<string, any>) : {};
}

function findStep(method: string, path: string, body: Record<string, any>): StepDefinition | undefined {
  const collection = DATABASE_PATH.exec(path)?.[1];
  if (collection) return DATABASE_STEPS[collection]?.[method];

  if (path === ACTION_LOG_PATH && method === 'post' && typeof body.actionId === 'string') {
    return ACTION_STEPS[body.actionId];
  }

  return undefined;
}

function isUnder(prefixes: string[], pagePath: string): boolean {
  return prefixes.some(prefix => pagePath === prefix || pagePath.startsWith(`${prefix}/`));
}

function resolveFlow(source: FlowSource, pagePath: string): Flow {
  if (source !== 'pela_rota') return source;
  return isUnder(ECOA_PAGE_PREFIXES, pagePath) ? 'ecoa' : 'realizacao_destaque';
}

function resolveStep(name: string, pagePath: string): string {
  if (name !== STATUS_CHANGE) return name;
  return isUnder(MANAGER_PAGE_PREFIXES, pagePath) ? 'apreciada' : 'editada';
}

function triggerStatus(responseData: unknown): unknown {
  const data = parseBody(responseData);
  return data.action?.attributes?.status ?? data.attributes?.status;
}

function outcomeOf(definition: StepDefinition, { httpStatus, responseData }: ApiResult): StepOutcome {
  if (httpStatus < 200 || httpStatus >= 300) return 'falha';
  if (!definition.readsTriggerOutcome) return 'sucesso';

  const status = triggerStatus(responseData);
  return typeof status === 'string' && status !== 'OK' ? 'recusado' : 'sucesso';
}

function stepDetails(name: string, body: Record<string, any>): Pick<FlowStepAttributes, 'status' | 'aprovado'> {
  if (name === 'apreciada' || name === 'editada') {
    return { status: typeof body.status === 'string' && ENUM_VALUE.test(body.status) ? body.status : 'outro' };
  }
  if (name === 'participantes_avaliados' || name === 'membro_avaliado') {
    return { aprovado: body.attributes?.approved === true ? 'sim' : 'nao' };
  }
  return {};
}

/** Devolve os atributos do evento `jogajunto.fluxo`, ou `null` quando a chamada não é etapa de fluxo. */
export function classifyFlowStep(call: ApiCall, result: ApiResult): FlowStepAttributes | null {
  const method = call.method.toLowerCase();
  const body = parseBody(call.body);
  const definition = findStep(method, apiPath(call.url), body);
  if (!definition) return null;

  const etapa = resolveStep(definition.step, call.pagePath);
  return {
    fluxo: resolveFlow(definition.flow, call.pagePath),
    etapa,
    resultado: outcomeOf(definition, result),
    http_status: String(result.httpStatus),
    ...stepDetails(etapa, body),
  };
}
